import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { formatCurrency } from '../../utils/currency';
import { toast } from '../../components/Toast';
import './Company.css';

const PLAN_ORDER = ['FREE', 'SILVER', 'GOLD', 'PLATINUM'];

function SubscriptionManagement() {
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);

  const loadData = () => {
    return Promise.all([
      axios.get('/company/subscription'),
      axios.get('/company/subscription/plans'),
      axios.get('/company/subscription/upgrade-request'),
    ]).then(([subRes, plansRes, reqRes]) => {
      setSubscription(subRes.data.data || subRes.data);
      // Normalize plans: use `name` field as `tier` for consistency
      const rawPlans = plansRes.data.data || plansRes.data;
      setPlans(rawPlans.map(p => ({ ...p, tier: p.tier || p.name })));
      setPendingRequest(reqRes.data.data || null);
    }).catch(err => {
      toast.error(err.response?.data?.message || 'Failed to load subscription data');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleRequestUpgrade = async (tier) => {
    setSubmitting(true);
    try {
      await axios.post('/company/subscription/request-upgrade', { newTier: tier });
      toast.success(`Upgrade request for ${tier} plan sent to super admin for approval.`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit upgrade request');
    } finally {
      setSubmitting(false);
      setConfirming(null);
    }
  };

  const currentTierIndex = subscription ? PLAN_ORDER.indexOf(subscription.planTier) : -1;

  if (loading) return <div className="co-loading">Loading subscription...</div>;

  return (
    <div className="co-page">
      <h1 className="co-title">Subscription Management</h1>

      {pendingRequest && (
        <div className="co-alert" style={{ background: '#fef3c7', border: '1px solid #f59e0b', color: '#92400e', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          ⏳ Upgrade request to <strong>{pendingRequest.requestedTier}</strong> plan is pending super admin approval.
        </div>
      )}

      {subscription && (
        <div className="co-sub-summary">
          <div className="co-sub-info">
            <span className="co-sub-label">Current Plan</span>
            <span className={`co-badge co-badge-${subscription.planTier?.toLowerCase()}`}>
              {subscription.planTier}
            </span>
          </div>
          <div className="co-sub-info">
            <span className="co-sub-label">Status</span>
            <span className={`co-badge co-badge-${subscription.status?.toLowerCase()}`}>
              {subscription.status}
            </span>
          </div>
          {subscription.trialEndAt && (
            <div className="co-sub-info">
              <span className="co-sub-label">Trial Ends</span>
              <span>{new Date(subscription.trialEndAt).toLocaleDateString()}</span>
            </div>
          )}
          {subscription.billingCycleEnd && (
            <div className="co-sub-info">
              <span className="co-sub-label">Next Billing</span>
              <span>{new Date(subscription.billingCycleEnd).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      )}

      {subscription && (
        <div className="co-usage">
          <h2>Usage</h2>
          <div className="co-usage-grid">
            <UsageBar label="Branches" used={subscription.branchCount} limit={subscription.branchLimit} />
            <UsageBar label="Users" used={subscription.userCount} limit={subscription.userLimit} />
          </div>
        </div>
      )}

      <h2 className="co-plans-title">Available Plans</h2>
      <div className="co-plans-grid">
        {plans.map(plan => {
          const planIndex = PLAN_ORDER.indexOf(plan.tier);
          const isCurrent = plan.tier === subscription?.planTier;
          const isUpgrade = planIndex > currentTierIndex;
          const hasPending = pendingRequest && pendingRequest.requestedTier === plan.tier;

          return (
            <div key={plan.tier} className={`co-plan-card co-plan-${plan.tier?.toLowerCase()} ${isCurrent ? 'current' : ''}`}>
              <div className="co-plan-tier-label">{plan.tier}</div>
              <div className="co-plan-chip">
                {isCurrent && <span className="co-current-badge">✓ Current Plan</span>}
                {hasPending && <span className="co-current-badge co-pending-badge">⏳ Pending</span>}
              </div>
              <div className="co-plan-price">
                {plan.monthlyPrice != null && plan.monthlyPrice > 0
                  ? `${formatCurrency(plan.monthlyPrice)}/mo`
                  : 'Free'}
              </div>
              <ul className="co-plan-features">
                {plan.features?.map(f => <li key={f}>✓ {f}</li>)}
              </ul>
              {!isCurrent && isUpgrade && (
                <button
                  className="co-btn co-btn-full co-btn-primary"
                  disabled={!!pendingRequest || submitting}
                  onClick={() => setConfirming({ tier: plan.tier })}
                >
                  {hasPending ? 'Approval Pending' : 'Request Upgrade'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {confirming && (
        <div className="co-modal-overlay">
          <div className="co-modal">
            <h2>Request Plan Upgrade</h2>
            <p>
              Send an upgrade request to <strong>{confirming.tier}</strong> plan to the super admin for approval?
            </p>
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>
              Your plan will be upgraded once the super admin approves the request.
            </p>
            <div className="co-modal-actions">
              <button className="co-btn" onClick={() => setConfirming(null)} disabled={submitting}>Cancel</button>
              <button
                className="co-btn co-btn-primary"
                disabled={submitting}
                onClick={() => handleRequestUpgrade(confirming.tier)}
              >
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UsageBar({ label, used, limit }) {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const unlimited = limit === -1;
  return (
    <div className="co-usage-item">
      <div className="co-usage-label">
        <span>{label}</span>
        <span>{used} / {unlimited ? '∞' : limit}</span>
      </div>
      {!unlimited && (
        <div className="co-usage-bar">
          <div className="co-usage-fill" style={{ width: `${pct}%`, background: pct > 80 ? '#ef4444' : '#667eea' }} />
        </div>
      )}
    </div>
  );
}

export default SubscriptionManagement;
